"use client";

import { useChartStable, useYScale } from "./chart-context";

export interface ReferenceAreaLabelProps {
  /** Upper y-value of the band (label anchors to this edge). */
  y2: number;
  /** Text shown inside the pill. */
  label: string;
  /** Y-axis id when the chart uses multiple y-scales. */
  yAxisId?: string | number;
  /** Horizontal position inside the plot area. Default "right". */
  align?: "left" | "right";
  /** Optional pill text color override. */
  color?: string;
}

const PILL_H = 20;
const PILL_INSET = 8;
const PILL_TOP_OFFSET = 4;

export function ReferenceAreaLabel({
  y2,
  label,
  yAxisId,
  align = "right",
  color,
}: ReferenceAreaLabelProps) {
  const { innerWidth, margin } = useChartStable();
  const yScale = useYScale(yAxisId);

  if (!label) return null;

  const yPx = yScale(y2);
  const yTop = yPx + PILL_TOP_OFFSET;
  const width = Math.max(40, label.length * 7 + 12);
  const x = align === "right" ? innerWidth - width - PILL_INSET : PILL_INSET;

  return (
    <g
      transform={`translate(${margin.left}, ${margin.top})`}
      style={{ pointerEvents: "none" }}
    >
      <foreignObject x={x} y={yTop} width={width} height={PILL_H}>
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 6px",
            fontSize: 11,
            lineHeight: 1,
            fontWeight: 500,
            color: color ?? "var(--chart-tooltip-foreground)",
            background: "var(--chart-tooltip-background)",
            backdropFilter: "blur(4px)",
            borderRadius: "var(--chart-tooltip-radius, 0.125rem)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </div>
      </foreignObject>
    </g>
  );
}

ReferenceAreaLabel.displayName = "ReferenceAreaLabel";

export default ReferenceAreaLabel;
